import type { StyleRule } from '@archipro-design/aria';
import { pxToRem, pxArrayToRem } from '@archipro-design/aria';
import { APPEAR_ANIMATION_DURATION } from '@modules/paint-picker-new/config';

export const PaintPickerWrapper: StyleRule<{
    paintPickerActive: boolean;
}> = ({ theme, paintPickerActive }) => ({
    position: 'fixed',
    left: 0,
    right: 0,
    top: 'auto',
    bottom: 0,
    zIndex: 2,
    pointerEvents: paintPickerActive ? 'all' : 'none',
    userSelect: 'none',

    [theme.screen.laptop]: {
        left: pxToRem(10),
        top: '50%',
        right: 'auto',
        bottom: 'auto',
        pointerEvents: 'all',
    },
});

export const PaintPicker: StyleRule = ({ theme }) => ({
    width: '100%',
    display: 'grid',
    gridTemplateAreas: '"top bottom" "main main"',
    gridTemplateColumns: '1fr 2fr',
    gridTemplateRows: 'auto 1fr',
    gridGap: pxArrayToRem([20, 0]),
    height: '100%',
    borderRadius: '20px 20px 0 0',
    background: '#fff',

    '&::after': {
        content: '""',
        position: 'absolute',
        top: pxToRem(12),
        left: '50%',
        transform: 'translateX(-50%)',
        width: pxToRem(40),
        height: pxToRem(4),
        borderRadius: '2px',
        background: 'rgba(85, 85, 85, 0.3)',
    },

    [theme.screen.laptop]: {
        gridTemplateAreas: '"top" "main" "bottom"',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridGap: 0,
        width: pxToRem(121),
        height: '100%',
        border: '1px solid #e4e4e4',
        borderRadius: '20px',

        '&::after': {
            display: 'none',
        },
    },
});

export const Top: StyleRule<{ paintPickerActive: boolean }> = ({
    theme,
    paintPickerActive,
}) => ({
    gridArea: 'top',
    alignContent: 'end',
    padding: pxArrayToRem([31, 0, 0, 15]),

    [theme.screen.laptop]: {
        padding: pxArrayToRem([27, 8, 15, 8]),

        '& > a': {
            cursor: paintPickerActive ? 'pointer' : 'default',
            pointerEvents: paintPickerActive ? 'unset' : 'none',
        },
    },
});

export const Main: StyleRule = () => ({
    gridArea: 'main',
});

export const Bottom: StyleRule<{ paintPickerActive: boolean }> = ({
    theme,
    paintPickerActive,
}) => ({
    gridArea: 'bottom',
    alignContent: 'end',
    alignSelf: 'end',
    justifySelf: 'end',
    padding: pxArrayToRem([0, 15, 10, 0]),

    [theme.screen.laptop]: {
        justifySelf: 'normal',
        display: 'flex',
        flexDirection: 'column',
        gap: pxToRem(13),
        borderTop: '1px solid #e4e4e4',
        padding: pxArrayToRem([11, 7, 28, 7]),
        cursor: paintPickerActive ? 'unset' : 'default',
    },
});

export const Logo: StyleRule = () => ({
    '& img': {
        width: '100%',
        background: '#fff',
    },
});

export const SavePaletteButton: StyleRule<{ paintPickerActive: boolean }> = ({
    theme,
    paintPickerActive,
}) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    padding: pxArrayToRem([7, 12, 6, 12]),
    fontSize: pxToRem(14),
    lineHeight: 1,
    fontWeight: 500,
    color: '#000',
    border: '1px solid #000',
    borderRadius: pxToRem(20),
    cursor: paintPickerActive ? 'pointer' : 'default',

    [theme.screen.laptop]: {
        width: '100%',
        padding: pxArrayToRem([8, 0]),
        fontSize: pxToRem(14),
    },
});

export const SavePaletteEmptyModal: StyleRule = ({ theme }) => ({
    position: 'absolute',
    bottom: pxToRem(270),
    left: 0,
    right: 0,

    [theme.screen.laptop]: {
        bottom: pxToRem(360),
        left: pxToRem(130),
        right: 'unset',
    },
});

export const CloseButton: StyleRule<{ paintPickerActive: boolean }> = ({
    paintPickerActive,
}) => ({
    cursor: paintPickerActive ? 'pointer' : 'default',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: pxToRem(14),
    lineHeight: 1,
    fontWeight: 400,
    color: '#676767',
    position: 'relative',
    left: pxToRem(-4),
});

export const CloseButtonIcon: StyleRule = () => ({
    width: pxToRem(16),
    height: pxToRem(16),
});

export const wrapperAnimation = {
    open: {
        left: [pxToRem(5), pxToRem(10)],
        transition: { duration: APPEAR_ANIMATION_DURATION * 0.8 },
    },
    closed: {
        left: [pxToRem(10), pxToRem(5)],
        transition: { duration: APPEAR_ANIMATION_DURATION * 0.6 },
    },
};

const transparentBg = 'rgba(255, 255, 255, 0)';
const opaqueBg = 'rgba(255, 255, 255, 1)';

export const containerAnimation = {
    open: {
        background: [transparentBg, transparentBg, opaqueBg],
        transition: { duration: APPEAR_ANIMATION_DURATION },
        transitionEnd: {
            borderWidth: pxToRem(1),
        },
    },
    closed: {
        background: [
            opaqueBg,
            transparentBg,
            transparentBg,
            transparentBg,
            transparentBg,
        ],
        borderWidth: [0, 0],
        transition: { duration: APPEAR_ANIMATION_DURATION },
    },
};

export const containerMobileAnimation = {
    open: {
        transform: ['translateY(100%)', 'translateY(0%)'],
        transition: { duration: APPEAR_ANIMATION_DURATION },
    },
    closed: {
        transform: ['translateY(0%)', 'translateY(100%)'],
        transition: { duration: APPEAR_ANIMATION_DURATION },
    },
};

export const topAnimation = {
    open: {
        opacity: [0, 0, 1],
        transition: {
            duration: APPEAR_ANIMATION_DURATION,
            delay: APPEAR_ANIMATION_DURATION * 0.5,
        },
    },
    closed: {
        opacity: [1, 0, 0],
        transition: { duration: APPEAR_ANIMATION_DURATION * 0.2 },
    },
};

export const bottomAnimation = {
    open: {
        opacity: [0, 0, 1],
        transition: {
            duration: APPEAR_ANIMATION_DURATION,
            delay: APPEAR_ANIMATION_DURATION * 0.5,
        },
    },
    closed: {
        opacity: [1, 0, 0],
        transition: { duration: APPEAR_ANIMATION_DURATION * 0.2 },
    },
};
